import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslatePipe } from '../../../../../core/pipes/translate.pipe';
import { TicketService } from '../../../../../core/services/ticket.service';
import { TicketCreationModalComponent } from '../../../../../shared/components/ticket-creation-modal/ticket-creation-modal';
import { TicketStatusBadgeComponent } from '../../../../../shared/components/ticket-status-badge';
import { Ticket } from '../../../../../core/models/ticket.model';

@Component({
  selector: 'app-seeker-tickets',
  standalone: true,
  imports: [CommonModule, TicketStatusBadgeComponent, TicketCreationModalComponent, TranslatePipe],
  template: `
    <div class="space-y-6">
      <div class="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 class="text-2xl font-bold text-text-primary">{{ 'ticket.myTickets' | translate }}</h2>
          <p class="mt-1 text-sm text-text-secondary">{{ tickets.length }} {{ 'ticket.myTickets' | translate }}</p>
        </div>
        <button (click)="showCreateModal = true" class="btn btn-primary text-sm shadow-glow flex items-center gap-2">
          <i class="fa-solid fa-plus"></i> {{ 'ticket.createNew' | translate }}
        </button>
      </div>

      @if (isLoading) {
        <div class="rounded-xl border border-border bg-surface shadow-elevated overflow-hidden">
          <div class="border-b border-border-strong bg-surface-elevated px-4 py-4">
            <div class="flex items-center gap-3">
              <span class="loader"></span>
              <span class="text-sm font-medium text-text-secondary">{{ 'ticket.myTickets' | translate }}</span>
            </div>
          </div>
          <div class="divide-y divide-border-subtle">
            @for (row of [1, 2, 3, 4]; track row) {
              <div class="grid grid-cols-5 gap-4 px-4 py-4">
                <div class="skeleton col-span-2 h-4"></div>
                <div class="skeleton h-4"></div>
                <div class="skeleton h-6 rounded-full"></div>
                <div class="skeleton h-8 rounded-lg"></div>
              </div>
            }
          </div>
        </div>
      } @else if (error) {
        <div class="alert alert-danger mx-auto max-w-xl rounded-xl shadow-elevated">
          <i class="fa-solid fa-triangle-exclamation mt-0.5"></i>
          <div>
            <p class="font-semibold text-text-primary">{{ 'ticket.myTickets' | translate }}</p>
            <p class="mt-1 text-sm text-text-secondary">{{ error }}</p>
          </div>
        </div>
      } @else if (tickets.length === 0) {
        <div class="rounded-xl border border-border bg-surface py-14 text-center shadow-elevated">
          <div class="flex flex-col items-center justify-center px-6">
            <div class="mb-4 flex h-14 w-14 items-center justify-center rounded-full border border-border-strong bg-surface-elevated text-text-muted">
              <i class="fa-solid fa-ticket text-xl"></i>
            </div>
            <h3 class="text-lg font-bold text-text-primary mb-1">{{ 'ticket.noTickets' | translate }}</h3>
            <p class="max-w-sm text-sm text-text-secondary">{{ 'ticket.noTicketsDesc' | translate }}</p>
          </div>
        </div>
      } @else {
        <div class="rounded-xl border border-border bg-surface shadow-elevated overflow-hidden">
          <div class="flex flex-col gap-3 border-b border-border-strong bg-surface-elevated px-4 py-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p class="text-sm font-semibold text-text-primary">{{ 'ticket.myTickets' | translate }}</p>
              <p class="mt-1 text-xs text-text-muted">{{ tickets.length }} total</p>
            </div>
            <span class="inline-flex w-max items-center gap-2 rounded-full border border-border-strong bg-surface px-3 py-1.5 text-xs font-medium text-text-secondary">
              <span class="h-1.5 w-1.5 rounded-full bg-primary shadow-glow"></span>
              Support
            </span>
          </div>

          <div class="hidden overflow-x-auto md:block">
            <table class="table-premium w-full min-w-[760px] text-left">
              <thead>
                <tr>
                  <th class="uppercase tracking-wider">Subject</th>
                  <th class="uppercase tracking-wider">Category</th>
                  <th class="uppercase tracking-wider">Status</th>
                  <th class="uppercase tracking-wider">Date</th>
                  <th class="text-right uppercase tracking-wider">Action</th>
                </tr>
              </thead>
              <tbody>
                @for (ticket of tickets; track ticket._id) {
                  <tr>
                    <td>
                      <p class="font-semibold text-text-primary text-sm">{{ ticket.subject }}</p>
                      <p class="text-xs text-text-muted mt-1 font-mono">{{ ticket._id | slice:-6 }}</p>
                    </td>
                    <td class="text-sm text-text-secondary">
                      <span class="badge badge-neutral capitalize">{{ ticket.category }}</span>
                    </td>
                    <td>
                      <app-ticket-status-badge [status]="ticket.status"></app-ticket-status-badge>
                    </td>
                    <td class="text-sm text-text-secondary font-mono">
                      {{ ticket.createdAt | date:'mediumDate' }}
                    </td>
                    <td class="text-right">
                      <button 
                        type="button"
                        (click)="selectedTicket = ticket" 
                        class="btn-icon text-text-muted hover:text-primary hover:bg-surface-elevated transition-colors" 
                        title="View Details"
                      >
                        <i class="fas fa-eye text-sm"></i>
                      </button>
                    </td>
                  </tr>
                }
              </tbody>
            </table>
          </div>

          <div class="divide-y divide-border-subtle md:hidden">
            @for (ticket of tickets; track ticket._id) {
              <article class="px-4 py-4 transition-colors hover:bg-surface-hover">
                <div class="mb-3 flex items-start justify-between gap-3">
                  <div class="min-w-0">
                    <p class="font-semibold text-text-primary">{{ ticket.subject }}</p>
                    <p class="mt-1 text-xs text-text-muted font-mono">{{ ticket._id | slice:-6 }}</p>
                  </div>
                  <app-ticket-status-badge [status]="ticket.status"></app-ticket-status-badge>
                </div>
                <div class="mb-4 grid grid-cols-2 gap-3 rounded-lg border border-border-subtle bg-surface-elevated p-3">
                  <div>
                    <p class="text-xs uppercase tracking-wider text-text-muted">Category</p>
                    <p class="mt-1 text-sm font-medium capitalize text-text-primary">{{ ticket.category }}</p>
                  </div>
                  <div>
                    <p class="text-xs uppercase tracking-wider text-text-muted">Date</p>
                    <p class="mt-1 text-sm text-text-secondary font-mono">{{ ticket.createdAt | date:'mediumDate' }}</p>
                  </div>
                </div>
                <button 
                  type="button"
                  (click)="selectedTicket = ticket" 
                  class="btn btn-secondary px-3 py-2 text-xs flex items-center gap-1.5" 
                  title="View Details"
                >
                  <i class="fas fa-eye"></i>
                  <span>View Details</span>
                </button>
              </article>
            }
          </div>
        </div>
      }

      <!-- Ticket Details Modal -->
      @if (selectedTicket) {
        <div 
          class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-fadeIn"
          (click)="selectedTicket = null"
        >
          <div 
            class="surface-card border border-border-strong rounded-3xl p-6 sm:p-8 max-w-lg w-full shadow-2xl relative space-y-6 max-h-[90vh] overflow-y-auto"
            (click)="$event.stopPropagation()"
          >
            <!-- Header -->
            <div class="flex items-start justify-between pb-4 border-b border-border-subtle">
              <div class="space-y-1">
                <div class="flex items-center gap-2">
                  <span class="px-2.5 py-0.5 rounded-md bg-primary-soft text-primary font-mono text-xs font-bold uppercase">
                    {{ selectedTicket.category }}
                  </span>
                  <app-ticket-status-badge [status]="selectedTicket.status"></app-ticket-status-badge>
                </div>
                <h3 class="text-lg font-bold text-text-primary tracking-tight mt-1">
                  {{ selectedTicket.subject }}
                </h3>
                <p class="text-xs font-mono text-text-muted">ID: {{ selectedTicket._id }}</p>
              </div>
              <button 
                type="button" 
                (click)="selectedTicket = null" 
                class="w-8 h-8 rounded-full bg-surface-elevated hover:bg-surface-hover border border-border flex items-center justify-center text-text-secondary hover:text-text-primary transition-colors shrink-0"
              >
                <i class="fa-solid fa-xmark text-xs"></i>
              </button>
            </div>

            <!-- Description -->
            <div class="space-y-2">
              <label class="block text-xs font-semibold text-text-muted uppercase tracking-wider">
                الوصف / التفاصيل
              </label>
              <div class="p-4 rounded-2xl bg-surface-elevated border border-border-subtle text-sm text-text-secondary whitespace-pre-wrap leading-relaxed">
                {{ selectedTicket.description }}
              </div>
            </div>

            <!-- Details Grid -->
            <div class="grid grid-cols-2 gap-3">
              <div class="p-3.5 rounded-2xl bg-surface-elevated border border-border-subtle">
                <p class="text-[11px] text-text-muted font-medium">القسم (Department)</p>
                <p class="text-xs font-semibold text-text-primary mt-1 capitalize">{{ selectedTicket.department || 'Support' }}</p>
              </div>
              <div class="p-3.5 rounded-2xl bg-surface-elevated border border-border-subtle">
                <p class="text-[11px] text-text-muted font-medium">تاريخ الإنشاء</p>
                <p class="text-xs font-semibold text-text-primary mt-1 font-mono">{{ selectedTicket.createdAt | date:'medium' }}</p>
              </div>
              @if (selectedTicket.relatedTo?.type) {
                <div class="p-3.5 rounded-2xl bg-surface-elevated border border-border-subtle col-span-2">
                  <p class="text-[11px] text-text-muted font-medium">مرتبط بـ (Related Entity)</p>
                  <p class="text-xs font-semibold text-text-primary mt-1 font-mono">
                    {{ selectedTicket.relatedTo.type }} : {{ selectedTicket.relatedTo.id }}
                  </p>
                </div>
              }
            </div>

            <!-- Footer -->
            <div class="flex items-center justify-end pt-4 border-t border-border-subtle">
              <button 
                type="button" 
                (click)="selectedTicket = null" 
                class="btn btn-secondary text-xs px-5 py-2.5 rounded-xl"
              >
                إغلاق
              </button>
            </div>
          </div>
        </div>
      }

      <!-- Create Ticket Modal -->
      <app-ticket-creation-modal 
        [show]="showCreateModal" 
        (close)="showCreateModal = false"
        (ticketCreated)="onTicketCreated($event)">
      </app-ticket-creation-modal>
    </div>
  `
})
export class SeekerTicketsComponent implements OnInit {
  tickets: Ticket[] = [];
  isLoading = true;
  error = '';
  showCreateModal = false;
  selectedTicket: Ticket | null = null;

  private ticketService = inject(TicketService);

  ngOnInit() {
    this.loadTickets();
  }

  loadTickets() {
    this.isLoading = true;
    this.ticketService.getUserTickets().subscribe({
      next: (res) => {
        this.tickets = res.tickets || [];
        this.isLoading = false;
      },
      error: (err) => {
        this.error = 'Failed to load tickets.';
        this.isLoading = false;
        console.error(err);
      }
    });
  }

  onTicketCreated(newTicket: Ticket) {
    this.showCreateModal = false;
    this.loadTickets();
  }
}
